/**
 * Idle Timeout Configuration Service
 * Manages system-wide idle timeout settings for automatic user logout
 */

import { supabase } from '../supabase/supabaseClient';
import { logger } from '../utils/logUtils';

export interface IdleTimeoutConfig {
  id?: string;
  timeout_minutes: number;
  warning_minutes: number;
  enabled: boolean;
  updated_at?: string;
  updated_by?: string;
}

export const DEFAULT_TIMEOUT_CONFIG: IdleTimeoutConfig = {
  timeout_minutes: 30,
  warning_minutes: 5,
  enabled: true
};

/**
 * Fetches the current idle timeout configuration
 * Returns default config if none exists
 */
export async function getIdleTimeoutConfig(): Promise<IdleTimeoutConfig> {
  try {
    const { data, error } = await supabase
      .from('system_idle_timeout_config')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      // Log the error but return defaults
      console.warn('[IdleTimeout] Error fetching config:', error);
      logger.info('No idle timeout config found, using defaults');
      return DEFAULT_TIMEOUT_CONFIG;
    }

    if (!data) {
      console.log('[IdleTimeout] No config found in database, using defaults');
      logger.info('No idle timeout config found, using defaults');
      return DEFAULT_TIMEOUT_CONFIG;
    }

    // Ensure we have integers, not strings
    const config: IdleTimeoutConfig = {
      ...data,
      timeout_minutes: typeof data.timeout_minutes === 'string'
        ? parseInt(data.timeout_minutes)
        : data.timeout_minutes,
      warning_minutes: typeof data.warning_minutes === 'string'
        ? parseInt(data.warning_minutes)
        : data.warning_minutes
    };

    console.log('[IdleTimeout] Config fetched from database:', config);
    return config;
  } catch (error) {
    console.error('[IdleTimeout] Exception fetching config:', error);
    logger.error('Error fetching idle timeout config:', error);
    return DEFAULT_TIMEOUT_CONFIG;
  }
}

/**
 * Updates the idle timeout configuration
 * Only admins should be able to call this
 */
export async function updateIdleTimeoutConfig(
  config: Omit<IdleTimeoutConfig, 'id' | 'updated_at' | 'updated_by'>
): Promise<{ success: boolean; error?: string }> {
  try {
    // Validate input
    if (config.timeout_minutes < 1 || config.timeout_minutes > 480) {
      return {
        success: false,
        error: 'Timeout must be between 1 and 480 minutes (8 hours)'
      };
    }

    if (config.warning_minutes < 1 || config.warning_minutes >= config.timeout_minutes) {
      return {
        success: false,
        error: 'Warning time must be between 1 minute and less than timeout duration'
      };
    }

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    // Check if config exists
    const { data: existingConfig } = await supabase
      .from('system_idle_timeout_config')
      .select('id')
      .limit(1)
      .single();

    if (existingConfig) {
      // Update existing config
      const { error } = await supabase
        .from('system_idle_timeout_config')
        .update({
          timeout_minutes: config.timeout_minutes,
          warning_minutes: config.warning_minutes,
          enabled: config.enabled,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingConfig.id);

      if (error) throw error;
    } else {
      // Insert new config
      const { error } = await supabase
        .from('system_idle_timeout_config')
        .insert({
          timeout_minutes: config.timeout_minutes,
          warning_minutes: config.warning_minutes,
          enabled: config.enabled,
          updated_by: user.id
        });

      if (error) throw error;
    }

    logger.info('Idle timeout config updated successfully', {
      timeout_minutes: config.timeout_minutes,
      warning_minutes: config.warning_minutes,
      enabled: config.enabled
    });

    return { success: true };
  } catch (error: any) {
    logger.error('Error updating idle timeout config:', error);
    return {
      success: false,
      error: error.message || 'Failed to update configuration'
    };
  }
}

/**
 * Converts minutes to milliseconds for timer usage
 */
export function minutesToMs(minutes: number): number {
  return minutes * 60 * 1000;
}

/**
 * Formats remaining time for display
 */
export function formatRemainingTime(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${seconds} second${seconds !== 1 ? 's' : ''}`;
}